export const PROJECT_FLY_BACK_STORAGE_KEY = 'lb:flyBack';
const DEFAULT_FLY_BACK_TTL_MS = 8000;

export interface IProjectTransitionPayload {
	center?: [number, number];
	projectId: string;
	ts: number;
	zoom?: number;
}

export interface IProjectTransitionRect {
	height: number;
	left: number;
	top: number;
	width: number;
}

export interface IProjectFlyAnimationOpts {
	activeOverlay?: boolean;
	contentEl?: HTMLElement | null;
	durationMs?: number;
	from: IProjectTransitionRect;
	to: IProjectTransitionRect;
}

export function projectTransitionProjectId(project: Pick<IProject, 'id' | 'slug'>): string {
	return project.slug || String(project.id);
}

export function setProjectSurfaceData(el: Element | null, project: Pick<IProject, 'id' | 'slug'>): void {
	if (el) {
		el.setAttribute('data-project-id', projectTransitionProjectId(project));
	}
}

export function projectSurfaceSelector(projectId: string): string {
	return `[data-project-id="${projectId.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`;
}

export function rectFromElement(el: Element): IProjectTransitionRect {
	const rect = el.getBoundingClientRect();
	return {
		height: rect.height,
		left: rect.left,
		top: rect.top,
		width: rect.width,
	};
}

export function writeProjectFlyBack(payload: Omit<IProjectTransitionPayload, 'ts'> & {ts?: number}): void {
	try {
		window.sessionStorage.setItem(
			PROJECT_FLY_BACK_STORAGE_KEY,
			JSON.stringify({
				...payload,
				ts: payload.ts || Date.now(),
			}));
	} catch {
	}
}

export function consumeProjectFlyBack(ttlMs: number = DEFAULT_FLY_BACK_TTL_MS): IProjectTransitionPayload | null {
	let raw = '';
	try {
		raw = window.sessionStorage.getItem(PROJECT_FLY_BACK_STORAGE_KEY) || '';
		window.sessionStorage.removeItem(PROJECT_FLY_BACK_STORAGE_KEY);
	} catch {
		return null;
	}
	if (!raw) {
		return null;
	}
	let payload: IProjectTransitionPayload;
	try {
		payload = <IProjectTransitionPayload>JSON.parse(raw);
	} catch {
		return null;
	}
	if (!payload.projectId || !payload.ts || (Date.now() - payload.ts) > ttlMs) {
		return null;
	}
	return payload;
}

export function waitAnimationFrames(count: number = 2): Promise<void> {
	return new Promise(resolve => {
		const step = (remaining: number): void => {
			if (remaining <= 0) {
				resolve();
				return;
			}
			window.requestAnimationFrame(() => step(remaining - 1));
		};
		step(count);
	});
}

function applyRect(el: HTMLElement, rect: IProjectTransitionRect, borderRadius: number | string): void {
	el.style.setProperty('top', `${rect.top}px`);
	el.style.setProperty('left', `${rect.left}px`);
	el.style.setProperty('width', `${rect.width}px`);
	el.style.setProperty('height', `${rect.height}px`);
	el.style.setProperty('border-radius', String(borderRadius));
}

export async function animateProjectFly(opts: IProjectFlyAnimationOpts): Promise<void> {
	const durationMs = opts.durationMs || 520;
	const overlay = document.createElement('div');
	overlay.className = 'lb-redesign-fly-overlay';
	const card = document.createElement('div');
	card.className = 'lb-redesign-fly-card';
	applyRect(card, opts.from, '14px');
	if (opts.contentEl) {
		const clone = <HTMLElement>opts.contentEl.cloneNode(true);
		clone.removeAttribute('id');
		card.appendChild(clone);
	}
	document.body.appendChild(overlay);
	document.body.appendChild(card);
	await waitAnimationFrames(2);
	if (opts.activeOverlay !== false) {
		overlay.classList.add('is-active');
	}
	applyRect(card, opts.to, opts.to.width === window.innerWidth && opts.to.height === window.innerHeight ? 0 : '14px');
	await new Promise(resolve => window.setTimeout(resolve, durationMs));
	card.remove();
	overlay.remove();
}
